import styled from "styled-components";
import { times, remove, isNumber } from "lodash";

import { Test } from "app/components/analytics/shared/type";
import { getEntries } from "app/components/analytics/shared/typeHelpers";
import Icon from "app/components/shared/Icon";
import IssueBadge from "app/components/analytics/shared/Issues/IssueBadge";
import CopyTextButton from "app/components/analytics/shared/CopyTextButton";
import { IssuesListProps } from "app/components/analytics/shared/Issues/getIssueContent";
import { track } from "app/lib/segmentAnalytics";
import Emojify from "app/components/shared/Emojify";

type RowTest = Omit<Test, "averageDuration" | "reliability" | "url">;

export type Props = {
  test: RowTest & {
    execution_id: number;
    execution: {
      issues_list: IssuesListProps;
      total_issues: number;
      duration: string;
      url: string;
    };
  };
  flakyTestAssignmentFeatureAvailable: boolean;
};

const StyledRow = styled.div<{ urgent?: boolean }>`
  position: relative;
  top: 0;
  border-radius: 8px;
  padding: 30px;
  transition: background 0.2s ease-in-out;
  cursor: pointer;
  border: 1px solid
    ${({ urgent }) => (urgent ? "var(--red-500)" : "var(--gray-500)")};
  ${({ urgent }) =>
    urgent &&
    `
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.01),
      0px 3px 3px 0px rgba(0, 0, 0, 0.02),
      0px 2px 2px 0px rgba(0, 0, 0, 0.04),
      0px 1px 0px 0px rgba(0, 0, 0, 0.09),
      0px 0px 0px 1px rgba(0, 0, 0, 0.09);
  `}

  &:not(:last-of-type) {
    margin-bottom: 20px;
  }

  &:hover {
    background: var(--slate-100);
  }

  .badge--gray {
    background: var(--slate-100);
    border: none;
    font-weight: 500;
    padding: 5px 8px;
    line-height: 1;
  }

  .title {
    overflow-wrap: break-word;

    :before {
      content: "";
      position: absolute;
      z-index: 0;
      inset: 0;
    }
  }
`;

const _trackIssueClicked = ({
  issues,
  href = "",
  test_id,
}: {
  issues: Array<string | number>;
  href?: string;
  test_id: number;
}) => {
  remove(issues, isNumber);

  track("Run Issue Row Clicked", {
    source: "Run Issue Row",
    action: "view-execution#show",
    issues: issues.toString(),
    href,
    test_id: test_id.toString(),
  });
};

const Row = ({ test, flakyTestAssignmentFeatureAvailable }: Props) => {
  const { issues_list, total_issues, duration } = test.execution;
  const isUrgent = issues_list.flaky === 1;
  const issues = getEntries(issues_list).filter(([, value]) => value > 0);

  return (
    <StyledRow urgent={isUrgent}>
      <div className="flex mb2 justify-between">
        <div>
          {issues.map(([issue]) => (
            <IssueBadge key={issue} issue={issue} urgent={issue === "flaky"} />
          ))}
          {flakyTestAssignmentFeatureAvailable && test.assigned_team?.name && (
            <span
              className="badge bg-light-purple purple semi-bold"
              style={{ padding: "2px 8px" }}
            >
              <Emojify text={`Assigned to ${test.assigned_team.name}`} />
            </span>
          )}
        </div>

        <div className="flex items-center">
          {times(total_issues, (index: number) => (
            <Icon
              key={index}
              icon="heroicons/outline/fire"
              style={{
                marginLeft: "2px",
                width: "24px",
                height: "24px",
                stroke: "var(--red-500)",
              }}
            />
          ))}
          <span className="ml1 badge badge--gray">{duration}</span>
        </div>

        <span className="hide">
          {total_issues} {total_issues > 1 ? "issues" : "issue"}
        </span>
      </div>

      <a
        href={test.execution.url}
        className="title block bold text-decoration-none color-inherit"
        data-testid="name"
        onClick={() =>
          _trackIssueClicked({
            href: test.execution.url,
            issues: issues.flat(),
            test_id: test.id,
          })
        }
      >
        {test.scope} {test.name}
      </a>

      {test.location && (
        <CopyTextButton
          text={test.location}
          styles={{
            padding: "6px 0 0 0",
            fontSize: "13px",
            color: "var(--charcoal-300)",
          }}
        />
      )}
    </StyledRow>
  );
};

export default Row;
